import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { LinkSimple, MagnifyingGlass, Trash, CheckCircle, WarningCircle, FloppyDisk, MagicWand, X, Database, UploadSimple, MapPinLine } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";

const inp = { width: "100%", padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13 };
const lbl = { fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", marginBottom: 4, display: "block" };
const badge = (ok) => ({
  fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 999,
  color: ok ? "#166534" : "#92400E", background: ok ? "#DCFCE7" : "#FEF3C7",
  border: "1px solid " + (ok ? "#BBF7D0" : "#FDE68A"), whiteSpace: "nowrap",
});

// SAP suggest input. Product = live type-ahead. Customer (manual) = type full code/name + Search
// (one clean query with retry — avoids ByD's intermittent empty stubs on partial type-ahead).
function SapSuggest({ kind, value, onChange, testid, manual = false }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [term, setTerm] = useState("");        // manual: the submitted term
  const [nonce, setNonce] = useState(0);        // force re-search on repeated Search clicks
  const trigger = manual ? term : value;
  useEffect(() => {
    const t0 = (trigger || "").trim();
    if (t0.length < 2) { setOpts([]); if (!manual) setSearched(false); return; }
    let cancel = false;
    setLoading(true);
    const run = () => api.get(`/sap/suggest/${kind}?q=${encodeURIComponent(t0)}`)
      .then((r) => { if (!cancel) setOpts(r.data || []); })
      .catch(() => { if (!cancel) setOpts([]); })
      .finally(() => { if (!cancel) { setLoading(false); setSearched(true); setOpen(true); } });
    const id = manual ? (run(), null) : setTimeout(run, 400);
    return () => { cancel = true; if (id) clearTimeout(id); };
  }, [trigger, kind, manual, nonce]);
  const doSearch = () => { setSearched(false); setTerm((value || "").trim()); setNonce((n) => n + 1); };
  const noHits = searched && !loading && opts.length === 0 && (trigger || "").trim().length >= 2;
  return (
    <div style={{ position: "relative" }}>
      <div style={{ display: "flex", gap: manual ? 6 : 0 }}>
        <input data-testid={testid} style={inp} value={value}
          onChange={(e) => { onChange(e.target.value); if (!manual) setOpen(true); }}
          onFocus={() => !manual && setOpen(true)}
          onKeyDown={(e) => { if (manual && e.key === "Enter") { e.preventDefault(); doSearch(); } }}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
          placeholder={kind === "product" ? "SAP Product ID (type to search SAP)" : "SAP Account ID — type full code/name, then Search"} />
        {manual && (
          <button type="button" data-testid={`${testid}-search`} className="jde-btn jde-btn-primary"
            style={{ whiteSpace: "nowrap" }} onMouseDown={(e) => { e.preventDefault(); doSearch(); }}>
            {loading ? "…" : "Search"}
          </button>
        )}
      </div>
      {open && (opts.length > 0 || loading) && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20, background: "#fff",
          border: "1px solid #E2E8F0", borderRadius: 8, marginTop: 3, maxHeight: 220, overflow: "auto", boxShadow: "0 8px 24px rgba(15,23,42,.12)" }}>
          {loading && <div style={{ padding: "8px 10px", fontSize: 12, color: "#94A3B8" }}>Searching SAP…</div>}
          {opts.map((o) => (
            <button key={o.id} type="button" data-testid={`${testid}-opt-${o.id}`}
              onMouseDown={(e) => { e.preventDefault(); onChange(o.id); setOpen(false); }}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "7px 10px", border: "none",
                borderBottom: "1px solid #F1F5F9", background: "#fff", cursor: "pointer", fontSize: 12.5 }}>
              <b style={{ color: "#0F172A" }}>{o.id}</b>{o.label ? <span style={{ color: "#64748B" }}> · {o.label}</span> : null}
            </button>
          ))}
        </div>
      )}
      {noHits && (
        <div data-testid={`${testid}-nohits`} style={{ fontSize: 11, color: "#B45309", marginTop: 4 }}>
          {kind === "customer"
            ? "No SAP match — try the full account code, search again, or enter the SAP Account ID manually."
            : "No SAP match — enter the SAP Product ID manually."}
        </div>
      )}
    </div>
  );
}

// One-click auto-map: scans open POs' unmapped codes against SAP live, auto-confirms EXACT
// matches, and surfaces the genuine mismatches (ambiguous / no SAP match) as a worklist.
function AutoMapModal({ onClose, onApplied }) {
  const [status, setStatus] = useState(null);
  const [applying, setApplying] = useState(false);
  const pollRef = useRef(null);

  const poll = () => api.get("/sap/map/auto/status").then((r) => {
    setStatus(r.data || {});
    if (r.data && r.data.running) pollRef.current = setTimeout(poll, 2000);
  }).catch(() => {});

  const start = async () => {
    setStatus({ running: true, progress: { done: 0, total: 0 }, result: null });
    try { await api.post("/sap/map/auto/scan"); } catch { /* ignore */ }
    poll();
  };

  useEffect(() => {
    api.get("/sap/map/auto/status").then((r) => {
      const d = r.data || {};
      setStatus(d);
      if (d.running) pollRef.current = setTimeout(poll, 1500);
      else if (!d.result) start();
    }).catch(() => start());
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const result = status?.result;
  const running = status?.running;
  const prog = status?.progress || { done: 0, total: 0 };
  const iWill = result?.items?.will_map || [];
  const cWill = result?.customers?.will_map || [];
  const iAmb = result?.items?.ambiguous || [];
  const iNone = result?.items?.unmatched || [];
  const cAmb = result?.customers?.ambiguous || [];
  const cNone = result?.customers?.unmatched || [];
  const willCount = iWill.length + cWill.length;
  const pct = prog.total ? Math.round((prog.done / prog.total) * 100) : 0;

  const apply = async () => {
    setApplying(true);
    try {
      const r = await api.post("/sap/map/auto/apply");
      toast.success(`Auto-mapped ${r.data.items_mapped} items · ${r.data.customers_mapped} customers`);
      onApplied?.();
      onClose();
    } catch { toast.error("Apply failed"); }
    finally { setApplying(false); }
  };

  const Stat = ({ label, n, tone }) => (
    <div style={{ flex: 1, textAlign: "center", padding: "10px 8px", borderRadius: 10, background: tone.bg, border: `1px solid ${tone.bd}` }}>
      <div style={{ fontSize: 22, fontWeight: 900, color: tone.fg }}>{n}</div>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#64748B", textTransform: "uppercase", marginTop: 2 }}>{label}</div>
    </div>
  );
  const Section = ({ title, will, amb, none, mapKey }) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: "#0F172A", marginBottom: 8 }}>{title}</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <Stat label="Exact — will map" n={will.length} tone={{ bg: "#DCFCE7", bd: "#BBF7D0", fg: "#166534" }} />
        <Stat label="Ambiguous" n={amb.length} tone={{ bg: "#FEF3C7", bd: "#FDE68A", fg: "#92400E" }} />
        <Stat label="No SAP match" n={none.length} tone={{ bg: "#FEE2E2", bd: "#FECACA", fg: "#991B1B" }} />
      </div>
      {will.length > 0 && (
        <div style={{ maxHeight: 130, overflow: "auto", border: "1px solid #E2E8F0", borderRadius: 8, marginBottom: 8 }}>
          {will.map((m) => (
            <div key={m.app_code} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 10px", borderBottom: "1px solid #F1F5F9", fontSize: 12 }}>
              <CheckCircle size={14} weight="fill" color="#16A34A" />
              <b style={{ color: "#0F172A" }}>{m.app_code}</b>
              <span style={{ color: "#94A3B8" }}>→</span>
              <b style={{ color: "#0369A1" }}>{m[mapKey]}</b>
              {m.src === "master" && <span style={{ fontSize: 9.5, fontWeight: 800, padding: "1px 6px", borderRadius: 999, background: "#EEF2FF", color: "#4338CA", border: "1px solid #C7D2FE" }}>MASTER</span>}
              {Array.isArray(m.sales_orgs) && m.sales_orgs.length > 0 && (
                <span style={{ fontSize: 10.5, color: "#64748B" }}>· sells: {m.sales_orgs.join(", ")}</span>
              )}
              {m.label && <span style={{ color: "#94A3B8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>· {m.label}</span>}
            </div>
          ))}
        </div>
      )}
      {(amb.length > 0 || none.length > 0) && (
        <details>
          <summary style={{ fontSize: 11.5, color: "#B45309", cursor: "pointer" }}>
            Needs manual mapping ({amb.length + none.length}) — ambiguous or no SAP match
          </summary>
          <div style={{ maxHeight: 120, overflow: "auto", fontSize: 11.5, color: "#475569", padding: "6px 4px" }}>
            {amb.map((a) => (
              <div key={a.app_code} style={{ padding: "3px 0" }}>
                <b>{a.app_code}</b> <span style={{ color: "#92400E" }}>ambiguous:</span> {(a.hits || []).map((h) => h.id).join(", ")}
              </div>
            ))}
            {none.map((c) => (
              <div key={c} style={{ padding: "3px 0" }}><b>{c}</b> <span style={{ color: "#991B1B" }}>no SAP match</span></div>
            ))}
          </div>
        </details>
      )}
    </div>
  );

  return (
    <div onMouseDown={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 50, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "48px 16px", overflow: "auto" }}>
      <div data-testid="automap-modal" onMouseDown={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "min(720px, 100%)", boxShadow: "0 20px 60px rgba(15,23,42,.3)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 18px", borderBottom: "1px solid #E2E8F0" }}>
          <MagicWand size={20} weight="bold" color="#0EA5E9" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#0F172A" }}>Auto-map exact matches</div>
            <div style={{ fontSize: 11.5, color: "#64748B" }}>Unmapped item &amp; customer codes on open POs, checked against SAP live. Only exact matches auto-map.</div>
          </div>
          <button data-testid="automap-close" onClick={onClose} className="jde-btn" style={{ padding: "6px 8px" }}><X size={15} weight="bold" /></button>
        </div>

        <div style={{ padding: 18 }}>
          {running && (
            <div style={{ padding: "10px 0 16px" }}>
              <div style={{ fontSize: 12.5, color: "#475569", marginBottom: 8 }}>Querying SAP… {prog.done}/{prog.total} codes</div>
              <div style={{ height: 8, background: "#E2E8F0", borderRadius: 999, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${pct}%`, background: "#0EA5E9", transition: "width .3s" }} />
              </div>
            </div>
          )}
          {status?.error && <div style={{ color: "#B91C1C", fontSize: 12.5, marginBottom: 10 }}>Error: {status.error}</div>}
          {result && (
            <>
              <Section title="Items (app item code → SAP product)" will={iWill} amb={iAmb} none={iNone} mapKey="sap_product" />
              <Section title="Customers (app code → SAP account)" will={cWill} amb={cAmb} none={cNone} mapKey="sap_account" />
            </>
          )}
          {!running && !result && !status?.error && <div style={{ fontSize: 12.5, color: "#64748B" }}>Starting scan…</div>}
        </div>

        <div style={{ display: "flex", gap: 10, padding: "14px 18px", borderTop: "1px solid #E2E8F0", justifyContent: "flex-end" }}>
          {!running && (
            <button data-testid="automap-rescan" className="jde-btn" onClick={start}>Re-scan</button>
          )}
          <button data-testid="automap-apply" className="jde-btn jde-btn-primary" disabled={running || applying || willCount === 0} onClick={apply}>
            {applying ? "Applying…" : `Apply ${willCount} exact mapping${willCount === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// One-click Walmart-Canada routing repair. Scan (read-only) lists what would change; Apply fixes the
// WMCC→WMCC account mapping and re-points not-yet-pushed Walmart-Canada POs off the US account WMRT.
function WalmartCaFix({ onApplied }) {
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [open, setOpen] = useState(false);

  const runScan = async () => {
    setLoading(true);
    try {
      const r = await api.get("/sap/walmart-ca/scan");
      setScan(r.data); setOpen(true);
    } catch { toast.error("Scan failed"); }
    finally { setLoading(false); }
  };
  const apply = async () => {
    if (!window.confirm("Fix Walmart-Canada routing?\n\nThis sets WMCC → SAP account WMCC and re-points not-yet-pushed Walmart-Canada POs. Already-pushed SOs are left untouched.")) return;
    setApplying(true);
    try {
      const r = await api.post("/sap/walmart-ca/fix");
      toast.success(`Fixed · mapping ${r.data.mapping_set} · ${r.data.pos_repointed} PO(s) re-pointed` + (r.data.pos_skipped_already_pushed ? ` · ${r.data.pos_skipped_already_pushed} already-pushed skipped` : ""));
      await runScan();
      onApplied?.();
    } catch { toast.error("Apply failed"); }
    finally { setApplying(false); }
  };

  const issues = scan ? (scan.map_fixes.length + scan.po_fix_count) : 0;
  return (
    <div data-testid="walmart-ca-card" style={{ border: "1px solid #FCD34D", background: "#FFFBEB", borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <MapPinLine size={16} weight="bold" color="#B45309" />
        <b style={{ fontSize: 13, color: "#92400E" }}>Walmart Canada routing</b>
        <span style={{ fontSize: 12, color: "#92400E" }}>Ensure Wal-Mart Canada Corp routes to SAP account <b>WMCC</b> (not US WMRT)</span>
        <button data-testid="walmart-ca-scan" className="jde-btn" onClick={runScan} disabled={loading} style={{ marginLeft: "auto" }}>
          {loading ? "Scanning…" : "Scan"}
        </button>
        {scan && issues > 0 && (
          <button data-testid="walmart-ca-apply" className="jde-btn jde-btn-primary" onClick={apply} disabled={applying}>
            {applying ? "Fixing…" : `Apply fix (${issues})`}
          </button>
        )}
      </div>
      {open && scan && (
        <div data-testid="walmart-ca-result" style={{ marginTop: 10, fontSize: 12.5, color: "#78350F" }}>
          {issues === 0 ? (
            <div data-testid="walmart-ca-clean">✓ All good — Walmart Canada already routes to WMCC. Nothing to fix.</div>
          ) : (
            <>
              {scan.map_fixes.map((m) => (
                <div key={m.app_code}>• Mapping <b>{m.app_code}</b>: <code>{m.from || "—"}</code> → <code>{m.to}</code></div>
              ))}
              <div style={{ marginTop: 4 }}>• <b>{scan.po_fix_count}</b> Walmart-Canada PO(s) currently resolving to WMRT{scan.po_pushed_count ? ` (${scan.po_pushed_count} already pushed — will be skipped)` : ""}:</div>
              <div style={{ maxHeight: 160, overflow: "auto", marginTop: 4, border: "1px solid #FDE68A", borderRadius: 6, background: "#fff" }}>
                {scan.po_fixes.slice(0, 50).map((p) => (
                  <div key={p._id} style={{ padding: "4px 8px", borderBottom: "1px solid #FEF3C7", display: "flex", gap: 8 }}>
                    <span style={{ fontWeight: 700 }}>{p.po}</span>
                    <span style={{ color: "#92400E" }}>{p.customer_name}</span>
                    <span style={{ marginLeft: "auto", color: "#B45309" }}>{p.ms_pcode} → resolves {p.resolves_to}{p.already_pushed ? " · pushed" : ""}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


export default function SapMapping() {
  const [tab, setTab] = useState("customers");
  const [autoOpen, setAutoOpen] = useState(false);
  const [master, setMaster] = useState({ total: 0, last_import: null });
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const [narrow, setNarrow] = useState(typeof window !== "undefined" && window.innerWidth < 900);
  useEffect(() => {
    const onR = () => setNarrow(window.innerWidth < 900);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  const gridCols = narrow ? "1fr" : "minmax(0, 360px) minmax(0, 1fr)";
  const loadMaster = () => api.get("/sap/map/material-master/summary").then((r) => setMaster(r.data || {})).catch(() => {});

  const onImportMaster = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/sap/map/material-master/import?replace=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.total_materials} SAP materials`);
      loadMaster();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  // ---- Customers ----
  const [customers, setCustomers] = useState([]);
  const [custMap, setCustMap] = useState([]);
  const [cq, setCq] = useState("");
  const [csel, setCsel] = useState(null); // {app_code, name}
  const [cform, setCform] = useState(null);

  // ---- Items ----
  const [itemMap, setItemMap] = useState([]);
  const [iq, setIq] = useState("");
  const [items, setItems] = useState([]);
  const [isel, setIsel] = useState(null);
  const [iform, setIform] = useState(null);

  const [busy, setBusy] = useState(false);

  const loadCustMap = () => api.get("/sap/map/customers").then((r) => setCustMap(r.data || [])).catch(() => setCustMap([]));
  const loadItemMap = () => api.get("/sap/map/items").then((r) => setItemMap(r.data || [])).catch(() => setItemMap([]));

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => setCustomers([]));
    loadCustMap();
    loadItemMap();
    loadMaster();
  }, []);

  const custMapByCode = useMemo(() => Object.fromEntries(custMap.map((m) => [m.app_code, m])), [custMap]);
  const itemMapByCode = useMemo(() => Object.fromEntries(itemMap.map((m) => [m.app_code, m])), [itemMap]);

  const filteredCustomers = useMemo(() => {
    const q = cq.trim().toLowerCase();
    return (customers || [])
      .filter((c) => c.ms_pcode) // must have an app code to map
      .filter((c) => !q || (c.name || "").toLowerCase().includes(q) || (c.ms_pcode || "").toLowerCase().includes(q))
      .sort((a, b) => {
        const am = custMapByCode[a.ms_pcode] ? 1 : 0, bm = custMapByCode[b.ms_pcode] ? 1 : 0;
        if (am !== bm) return am - bm; // unmapped first
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [customers, cq, custMapByCode]);

  const pickCustomer = (c) => {
    const m = custMapByCode[c.ms_pcode] || {};
    setCsel({ app_code: c.ms_pcode, name: c.name });
    setCform({
      app_code: c.ms_pcode,
      sap_account: m.sap_account || c.ms_pcode,
      sales_unit: m.sales_unit || "P9-SAL",
      distribution_channel: m.distribution_channel || "01",
      label: m.label || c.name || "",
    });
  };

  const saveCustMap = async () => {
    if (!cform?.sap_account?.trim()) { toast.error("Enter the SAP Account ID"); return; }
    setBusy(true);
    try {
      await api.post("/sap/map/customers", cform);
      await loadCustMap();
      toast.success(`Mapped ${cform.app_code} → SAP ${cform.sap_account}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const delCustMap = async (code) => {
    setBusy(true);
    try { await api.delete(`/sap/map/customers/${encodeURIComponent(code)}`); await loadCustMap();
      if (csel?.app_code === code) setCform((f) => ({ ...f, sap_account: "", sales_unit: "", distribution_channel: "" }));
      toast.success("Mapping removed");
    } catch { toast.error("Delete failed"); } finally { setBusy(false); }
  };

  // Item search (ERP master)
  useEffect(() => {
    const term = iq.trim();
    if (term.length < 2) { setItems([]); return; }
    let cancel = false;
    const t = setTimeout(() => {
      api.get(`/portal/items?q=${encodeURIComponent(term)}`)
        .then((r) => { if (!cancel) setItems(r.data || []); })
        .catch(() => { if (!cancel) setItems([]); });
    }, 350);
    return () => { cancel = true; clearTimeout(t); };
  }, [iq]);

  const pickItem = (it) => {
    const m = itemMapByCode[it.icode] || {};
    setIsel({ app_code: it.icode, name: it.iname });
    setIform({ app_code: it.icode, sap_product: m.sap_product || "", label: m.label || it.iname || "" });
  };
  const pickItemFromMap = (m) => {
    setIsel({ app_code: m.app_code, name: m.label });
    setIform({ app_code: m.app_code, sap_product: m.sap_product || "", label: m.label || "" });
  };

  const saveItemMap = async () => {
    if (!iform?.sap_product?.trim()) { toast.error("Enter the SAP Product ID"); return; }
    setBusy(true);
    try {
      await api.post("/sap/map/items", iform);
      await loadItemMap();
      toast.success(`Mapped ${iform.app_code} → SAP ${iform.sap_product}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const delItemMap = async (code) => {
    setBusy(true);
    try { await api.delete(`/sap/map/items/${encodeURIComponent(code)}`); await loadItemMap();
      if (isel?.app_code === code) setIform((f) => ({ ...f, sap_product: "" }));
      toast.success("Mapping removed");
    } catch { toast.error("Delete failed"); } finally { setBusy(false); }
  };

  const TabBtn = ({ id, label, count }) => (
    <button data-testid={`sap-map-tab-${id}`} onClick={() => setTab(id)}
      style={{ padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700,
        border: "1px solid " + (tab === id ? "#0EA5E9" : "#E2E8F0"), background: tab === id ? "#F0F9FF" : "#fff",
        color: tab === id ? "#0369A1" : "#475569" }}>
      {label} <span style={{ opacity: .7 }}>· {count}</span>
    </button>
  );

  return (
    <Layout breadcrumb={["Setup", "SAP Mapping"]}>
      <div data-testid="sap-mapping-page" style={{ maxWidth: 1120, margin: "0 auto", padding: "8px 4px 40px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <LinkSimple size={22} weight="bold" color="#0EA5E9" />
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: "#0F172A" }}>SAP Mapping</h1>
        </div>
        <p style={{ fontSize: 13, color: "#475569", margin: "0 0 8px" }}>
          Map your app <b>Customer codes → SAP Account IDs</b> and <b>Item codes → SAP Product IDs</b>.
          The SAP Push page auto-resolves these so you never re-type SAP IDs.
        </p>
        <div style={{ fontSize: 12, color: master.total ? "#166534" : "#94A3B8", margin: "0 0 14px", display: "flex", alignItems: "center", gap: 6 }}>
          <Database size={14} weight="bold" />
          <span>SAP material master: <b>{master.total || 0}</b> materials{master.last_import ? ` · imported ${String(master.last_import).slice(0, 10)}` : " · not imported yet"}</span>
          {master.total > 0 && <span style={{ color: "#64748B" }}>· Auto-map resolves offline from this (incl. sales-org validation)</span>}
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
          <TabBtn id="customers" label="Customers" count={custMap.length} />
          <TabBtn id="items" label="Items" count={itemMap.length} />
          <input ref={fileRef} type="file" accept=".csv" data-testid="sap-map-master-file" style={{ display: "none" }} onChange={onImportMaster} />
          <button data-testid="sap-map-master-import" className="jde-btn" onClick={() => fileRef.current?.click()} disabled={importing}
            style={{ marginLeft: "auto", whiteSpace: "nowrap" }}>
            <UploadSimple size={15} weight="bold" style={{ marginRight: 6, verticalAlign: "-2px" }} />
            {importing ? "Importing…" : "Import SAP material master"}
          </button>
          <button data-testid="sap-map-automap" className="jde-btn jde-btn-primary" onClick={() => setAutoOpen(true)}
            style={{ whiteSpace: "nowrap" }}>
            <MagicWand size={15} weight="bold" style={{ marginRight: 6, verticalAlign: "-2px" }} />
            Auto-map exact matches
          </button>
        </div>

        {autoOpen && <AutoMapModal onClose={() => setAutoOpen(false)} onApplied={() => { loadItemMap(); loadCustMap(); }} />}

        <WalmartCaFix onApplied={() => loadCustMap()} />

        {tab === "customers" && (
          <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 18 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ position: "relative", marginBottom: 10 }}>
                <MagnifyingGlass size={15} weight="bold" color="#94A3B8" style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)" }} />
                <input data-testid="sap-map-cust-search" value={cq} onChange={(e) => setCq(e.target.value)} placeholder="Search app customer / code…" style={{ ...inp, paddingLeft: 32 }} />
              </div>
              <div style={{ display: "grid", gap: 7, maxHeight: 560, overflow: "auto" }}>
                {filteredCustomers.map((c) => {
                  const mapped = !!custMapByCode[c.ms_pcode];
                  return (
                    <button key={c.id || c.ms_pcode} data-testid={`sap-map-cust-${c.ms_pcode}`} onClick={() => pickCustomer(c)}
                      style={{ textAlign: "left", padding: "9px 11px", borderRadius: 9, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, minWidth: 0, width: "100%",
                        border: "1px solid " + (csel?.app_code === c.ms_pcode ? "#0EA5E9" : "#E2E8F0"), background: csel?.app_code === c.ms_pcode ? "#F0F9FF" : "#fff" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: "#0F172A" }}>{c.ms_pcode}</div>
                        <div style={{ fontSize: 11.5, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                      </div>
                      <span style={{ ...badge(mapped), flexShrink: 0 }}>{mapped ? `→ ${custMapByCode[c.ms_pcode].sap_account}` : "Unmapped"}</span>
                    </button>
                  );
                })}
                {filteredCustomers.length === 0 && <div className="muted" style={{ fontSize: 12, padding: 10 }}>No customers.</div>}
              </div>
            </div>

            <div style={{ minWidth: 0 }}>
              {!cform ? (
                <div className="muted" style={{ padding: 30, textAlign: "center", border: "1px dashed #E2E8F0", borderRadius: 12 }}>
                  Pick an app customer on the left to map it to a SAP Account.
                </div>
              ) : (
                <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: 18, background: "#fff" }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "#0F172A", marginBottom: 2 }}>{csel.name}</div>
                  <div style={{ fontSize: 12, color: "#64748B", marginBottom: 16 }}>App customer code: <b>{cform.app_code}</b></div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={lbl}>SAP Account ID *</label>
                      <SapSuggest kind="customer" testid="sap-map-cust-account" manual value={cform.sap_account} onChange={(v) => setCform((f) => ({ ...f, sap_account: v }))} />
                    </div>
                    <div><label style={lbl}>Sales Unit / Sales Org</label><input data-testid="sap-map-cust-salesunit" style={inp} value={cform.sales_unit} onChange={(e) => setCform((f) => ({ ...f, sales_unit: e.target.value }))} placeholder="e.g. P9-SAL" /></div>
                    <div><label style={lbl}>Distribution Channel</label><input data-testid="sap-map-cust-dc" style={inp} value={cform.distribution_channel} onChange={(e) => setCform((f) => ({ ...f, distribution_channel: e.target.value }))} placeholder="e.g. 01" /></div>
                    <div style={{ gridColumn: "1 / -1" }}><label style={lbl}>Label / note</label><input data-testid="sap-map-cust-label" style={inp} value={cform.label} onChange={(e) => setCform((f) => ({ ...f, label: e.target.value }))} /></div>
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                    <button data-testid="sap-map-cust-save" className="jde-btn jde-btn-primary" disabled={busy} onClick={saveCustMap}><FloppyDisk size={15} weight="bold" style={{ marginRight: 6, verticalAlign: "-2px" }} />Save mapping</button>
                    {custMapByCode[cform.app_code] && <button data-testid="sap-map-cust-delete" className="jde-btn" disabled={busy} onClick={() => delCustMap(cform.app_code)}><Trash size={15} weight="bold" style={{ marginRight: 6, verticalAlign: "-2px" }} />Remove</button>}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "items" && (
          <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 18 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ position: "relative", marginBottom: 10 }}>
                <MagnifyingGlass size={15} weight="bold" color="#94A3B8" style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)" }} />
                <input data-testid="sap-map-item-search" value={iq} onChange={(e) => setIq(e.target.value)} placeholder="Search app item code / name (min 2 chars)…" style={{ ...inp, paddingLeft: 32 }} />
              </div>
              {iq.trim().length >= 2 ? (
                <div style={{ display: "grid", gap: 7, maxHeight: 300, overflow: "auto", marginBottom: 14 }}>
                  {items.map((it) => {
                    const mapped = !!itemMapByCode[it.icode];
                    return (
                      <button key={it.icode} data-testid={`sap-map-item-${it.icode}`} onClick={() => pickItem(it)}
                        style={{ textAlign: "left", padding: "9px 11px", borderRadius: 9, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, minWidth: 0, width: "100%",
                          border: "1px solid " + (isel?.app_code === it.icode ? "#0EA5E9" : "#E2E8F0"), background: isel?.app_code === it.icode ? "#F0F9FF" : "#fff" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 800, color: "#0F172A" }}>{it.icode}</div>
                          <div style={{ fontSize: 11.5, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.iname}</div>
                        </div>
                        <span style={{ ...badge(mapped), flexShrink: 0 }}>{mapped ? `→ ${itemMapByCode[it.icode].sap_product}` : "Unmapped"}</span>
                      </button>
                    );
                  })}
                  {items.length === 0 && <div className="muted" style={{ fontSize: 12, padding: 10 }}>No items found in the ERP master.</div>}
                </div>
              ) : (
                <div className="muted" style={{ fontSize: 12, padding: "6px 4px 14px" }}>Type at least 2 characters to search the ERP item master.</div>
              )}

              <label style={lbl}>Existing item mappings ({itemMap.length})</label>
              <div style={{ display: "grid", gap: 6, maxHeight: 240, overflow: "auto" }}>
                {itemMap.map((m) => (
                  <button key={m.app_code} data-testid={`sap-map-item-existing-${m.app_code}`} onClick={() => pickItemFromMap(m)}
                    style={{ textAlign: "left", padding: "7px 10px", borderRadius: 8, cursor: "pointer", fontSize: 12,
                      border: "1px solid " + (isel?.app_code === m.app_code ? "#0EA5E9" : "#E2E8F0"), background: "#fff" }}>
                    <b style={{ color: "#0F172A" }}>{m.app_code}</b> <span style={{ color: "#94A3B8" }}>→</span> <b style={{ color: "#0369A1" }}>{m.sap_product}</b>
                  </button>
                ))}
                {itemMap.length === 0 && <div className="muted" style={{ fontSize: 12 }}>No item mappings yet.</div>}
              </div>
            </div>

            <div style={{ minWidth: 0 }}>
              {!iform ? (
                <div className="muted" style={{ padding: 30, textAlign: "center", border: "1px dashed #E2E8F0", borderRadius: 12 }}>
                  Search & pick an app item on the left to map it to a SAP Product.
                </div>
              ) : (
                <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: 18, background: "#fff" }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "#0F172A", marginBottom: 2 }}>{isel?.name || iform.app_code}</div>
                  <div style={{ fontSize: 12, color: "#64748B", marginBottom: 16 }}>App item code: <b>{iform.app_code}</b></div>
                  <div style={{ display: "grid", gap: 14 }}>
                    <div>
                      <label style={lbl}>SAP Product ID *</label>
                      <SapSuggest kind="product" testid="sap-map-item-product" value={iform.sap_product} onChange={(v) => setIform((f) => ({ ...f, sap_product: v }))} />
                    </div>
                    <div><label style={lbl}>Label / note</label><input data-testid="sap-map-item-label" style={inp} value={iform.label} onChange={(e) => setIform((f) => ({ ...f, label: e.target.value }))} /></div>
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                    <button data-testid="sap-map-item-save" className="jde-btn jde-btn-primary" disabled={busy} onClick={saveItemMap}><FloppyDisk size={15} weight="bold" style={{ marginRight: 6, verticalAlign: "-2px" }} />Save mapping</button>
                    {itemMapByCode[iform.app_code] && <button data-testid="sap-map-item-delete" className="jde-btn" disabled={busy} onClick={() => delItemMap(iform.app_code)}><Trash size={15} weight="bold" style={{ marginRight: 6, verticalAlign: "-2px" }} />Remove</button>}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
